import { readSse } from '@/transport/sse';
/** One SSE frame type the server sends about the stream itself, not the chat. */
const STREAM_OPEN = 'stream.open';
const STREAM_CLOSED = 'stream.closed';
/**
 * One chat's live events, kept connected.
 *
 * Reconnects on its own and resumes from the last event id, so a dropped
 * connection loses nothing. When the token expires the server ends the stream
 * and this reconnects with a fresh one. A 404/403 (not this visitor's chat) or
 * repeated failures (streaming off, Redis down) stop it: the transport then
 * answers from the POST alone, exactly as before streaming existed, and tries
 * the stream again on the next message.
 */
export class AgentEventStream {
    options;
    controller;
    lastEventId;
    stopped = false;
    ready = false;
    failures = 0;
    waiters = [];
    /** When it gave up, so the transport can wait before trying again. */
    stoppedAt = 0;
    constructor(options) {
        this.options = options;
        this.lastEventId = options.from;
    }
    /** Start connecting. Safe to call once; later calls are no-ops. */
    start() {
        void this.run();
    }
    /** Still trying (or connected). False once it has given up or been closed. */
    get alive() {
        return !this.stopped;
    }
    /**
     * True once the server has confirmed the stream (`stream.open`), so a
     * message sent now will have its reply streamed. False if that does not
     * happen within `timeoutMs` — the caller then just doesn't stream this one.
     */
    waitReady(timeoutMs) {
        if (this.ready)
            return Promise.resolve(true);
        if (this.stopped)
            return Promise.resolve(false);
        return new Promise((resolve) => {
            const timer = setTimeout(() => settle(false), timeoutMs);
            const settle = (value) => {
                clearTimeout(timer);
                this.waiters = this.waiters.filter((w) => w !== settle);
                resolve(value);
            };
            this.waiters.push(settle);
        });
    }
    close() {
        this.stopped = true;
        this.ready = false;
        this.controller?.abort();
        this.notify(false);
    }
    notify(ready) {
        const waiters = this.waiters;
        this.waiters = [];
        for (const waiter of waiters)
            waiter(ready);
    }
    async run() {
        const maxFailures = this.options.maxFailures ?? 3;
        let forceAuth = false;
        while (!this.stopped) {
            const outcome = await this.connectOnce(forceAuth);
            this.ready = false;
            if (this.stopped)
                return;
            // Anyone waiting to send is not kept waiting through the retries: this
            // message goes without streaming, and the next may find it connected.
            if (outcome !== 'ended' && outcome !== 'expired')
                this.notify(false);
            forceAuth = outcome === 'expired' || outcome === 'unauthorized';
            if (outcome === 'gone') {
                this.options.logger.debug('live stream refused; replies arrive whole', { url: this.options.url });
                break;
            }
            if (outcome === 'ended' || outcome === 'expired') {
                // A clean close (token expiry, a server restart): reconnect at once.
                this.failures = 0;
                continue;
            }
            this.failures += 1;
            if (this.failures >= maxFailures) {
                this.options.logger.debug('live stream unavailable; replies arrive whole', { failures: this.failures });
                break;
            }
            const delay = Math.min(1000 * 2 ** (this.failures - 1), this.options.maxBackoffMs ?? 15_000);
            await new Promise((r) => setTimeout(r, delay));
        }
        this.stopped = true;
        this.stoppedAt = Date.now();
        this.notify(false);
    }
    async connectOnce(forceAuth) {
        const controller = new AbortController();
        this.controller = controller;
        try {
            const headers = {
                ...(await this.options.headers(forceAuth)),
                Accept: 'text/event-stream',
            };
            if (this.lastEventId)
                headers['Last-Event-ID'] = this.lastEventId;
            const response = await this.options.fetchImpl(this.options.url, {
                method: 'GET',
                headers,
                signal: controller.signal,
                cache: 'no-store',
            });
            if (response.status === 401)
                return 'unauthorized';
            if (response.status === 403 || response.status === 404)
                return 'gone';
            const type = response.headers.get('content-type') ?? '';
            if (!response.ok || !response.body || !type.includes('text/event-stream'))
                return 'failed';
            let opened = false;
            for await (const frame of readSse(response.body)) {
                if (frame.id)
                    this.lastEventId = frame.id;
                if (frame.event === STREAM_OPEN) {
                    opened = true;
                    this.ready = true;
                    this.failures = 0;
                    this.notify(true);
                    continue;
                }
                if (frame.event === STREAM_CLOSED)
                    return 'expired';
                let event;
                try {
                    event = JSON.parse(frame.data);
                }
                catch {
                    continue; // a malformed frame is skipped, not fatal
                }
                if (!event.type)
                    event.type = frame.event;
                this.options.onEvent(event);
            }
            // A stream that closed before it ever opened is a failure, not a clean
            // end: reconnecting at once would spin.
            return opened ? 'ended' : 'failed';
        }
        catch (err) {
            if (this.stopped)
                return 'ended';
            this.options.logger.debug('live stream dropped', {
                error: err instanceof Error ? err.message : String(err),
            });
            return 'failed';
        }
    }
}
